import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Layers, Eye, EyeOff, ZoomIn, X, Check, Globe, ChevronDown, ChevronRight, Lock, ListTree } from 'lucide-react';
import * as Cesium from 'cesium';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useCesium } from '../../context/CesiumContext';
import { useAssetSettings } from '@/hooks/use-asset-settings';
import { useParams } from 'react-router';
import { useIfcFeatures } from '../../context/IfcFeatureContext';

type LayerType = 'primitive' | 'datasource';

interface LayerData {
	id: string;
	uniqueKey?: string;
	type: LayerType;
	name: string;
	visible: boolean;
	/** Null while the layer is deferred — nothing has been fetched for it yet. */
	reference: Cesium.Cesium3DTileset | Cesium.DataSource | null;
	thumbnail?: string;
	isSubSurface?: boolean;
	/** Known layer that is switched off, so its tileset was never requested. */
	deferred?: boolean;
	/** Conversion pipeline of a tile-server tileset (`bim` gets the Structure button). */
	tilesetType?: string | null;
}

interface LayerCategory {
	id: string;
	label: string;
	icon: React.ReactNode;
	items: LayerData[];
}

interface BaseMapOption {
	id: string;
	name: string;
	thumb: string;
	description: string;
	handler: (viewer: Cesium.Viewer, activeRef: React.MutableRefObject<any>) => Promise<void>;
}

const ensureWorldTerrain = async (viewer: Cesium.Viewer) => {
	if (viewer.isDestroyed()) return;
	viewer.scene.globe.show = true;
	if (!(viewer.terrainProvider instanceof Cesium.CesiumTerrainProvider)) {
		viewer.terrainProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(1);
	}
};

const ionImageryHandler = (assetId: number) => async (viewer: Cesium.Viewer) => {
	if (viewer.isDestroyed()) return;
	await ensureWorldTerrain(viewer);
	if (viewer.isDestroyed()) return;
	viewer.imageryLayers.removeAll();
	const provider = await Cesium.IonImageryProvider.fromAssetId(assetId);
	viewer.imageryLayers.addImageryProvider(provider);
};

const getBasemapOptions = (t: TFunction): BaseMapOption[] => [
	{
		id: 'cesium-world',
		name: t('viewer.layers.basemaps.cesiumWorld.name'),
		thumb: '',
		description: t('viewer.layers.basemaps.cesiumWorld.description'),
		handler: async (viewer) => {
			if (viewer.isDestroyed()) return;
			viewer.scene.globe.show = true;
			viewer.imageryLayers.removeAll();
			viewer.terrainProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(1);
			const imageryProvider = await Cesium.createWorldImageryAsync({
				style: Cesium.IonWorldImageryStyle.AERIAL,
			});
			if (!viewer.isDestroyed()) {
				viewer.imageryLayers.addImageryProvider(imageryProvider);
			}
		},
	},
	{
		id: 'google-3d-official',
		name: t('viewer.layers.basemaps.googlePhotorealistic.name'),
		thumb: '',
		description: t('viewer.layers.basemaps.googlePhotorealistic.description'),
		handler: async (viewer, activeRef) => {
			if (viewer.isDestroyed()) return;

			viewer.imageryLayers.removeAll();
			viewer.scene.globe.show = false; // Hide the base globe (ellipsoid)

			// Remove previous primitive if exists
			if (activeRef?.current && !activeRef.current.isDestroyed?.()) {
				viewer.scene.primitives.remove(activeRef.current);
				activeRef.current = null;
			}

			try {
				const tileset = await Cesium.createGooglePhotorealistic3DTileset();
				if (!viewer.isDestroyed()) {
					(tileset as any)._isGoogleTileset = true;

					// Lower SSE forces higher LODs (Level of Detail) to load.
					// Default is 16. Lowering to 8 or 4 dramatically improves quality at the cost of network/GPU.
					tileset.maximumScreenSpaceError = 4;

					// 2. Increase cache size so high-res tiles don't get evicted immediately.
					// Default is 512 (MB). Increase to 2048 (2GB) or 4096 (4GB) for heavy Google Maps data.
					// tileset.maximumMemoryUsage = 2048;

					// 3. Optional: Preload flight paths if you use viewer.flyTo() a lot
					tileset.preloadWhenHidden = true;
					// ==========================================
					// CACHE (modern replacement of maximumMemoryUsage)
					tileset.cacheBytes = 2 * 1024 * 1024 * 1024; // 2GB
					tileset.maximumCacheOverflowBytes = 1 * 1024 * 1024 * 1024; // +1GB buffer
					// tileset.immediatelyLoadDesiredLevelOfDetail = true; // Load high LODs immediately when camera moves

					viewer.scene.primitives.add(tileset);
					activeRef.current = tileset;
				}
			} catch (error) {
				console.error('Failed to load Google Tiles', error);
			}
		},
	},
	// {
	// 	id: 'google-3d',
	// 	name: 'Google 3D Tiles',
	// 	thumb: '',
	// 	description: 'Photorealistic Mesh',
	// 	handler: async (viewer, activeRef) => {
	// 		if (viewer.isDestroyed()) return;
	// 		viewer.imageryLayers.removeAll();
	// 		viewer.scene.globe.show = false;

	// 		if (activeRef?.current && !activeRef.current.isDestroyed?.()) {
	// 			viewer.scene.primitives.remove(activeRef.current);
	// 			activeRef.current = null;
	// 		}

	// 		try {
	// 			const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(2275207);
	// 			if (!viewer.isDestroyed()) {
	// 				(tileset as any)._isGoogleTileset = true;
	// 				viewer.scene.primitives.add(tileset);
	// 				activeRef.current = tileset;
	// 			}
	// 		} catch (error) {
	// 			console.error('Failed to load Google Tiles', error);
	// 		}
	// 	},
	// },
	{
		id: 'bing-aerial',
		name: t('viewer.layers.basemaps.bingAerial.name'),
		thumb: '',
		description: t('viewer.layers.basemaps.bingAerial.description'),
		handler: ionImageryHandler(2),
	},
	{
		id: 'bing-roads',
		name: t('viewer.layers.basemaps.bingRoads.name'),
		thumb: '',
		description: t('viewer.layers.basemaps.bingRoads.description'),
		handler: ionImageryHandler(4),
	},
];

interface LayerControlProps {
	selectedBaseMapId?: string;
	onBaseMapChange?: (id: string) => void;
}

const LayerControl: React.FC<LayerControlProps> = ({ selectedBaseMapId = 'cesium-world', onBaseMapChange }) => {
	const { t } = useTranslation();
	const basemapOptions = useMemo(() => getBasemapOptions(t), [t]);
	const { assetId } = useParams();
	const { updateSetting, getSettings } = useAssetSettings(assetId);

	const {
		viewer,
		tilesLoaded,
		undergroundEnabled,
		setUndergroundEnabled,
		subSurfaceLayers,
		deferredLayers,
		pendingLayerLoads,
		requestLayerLoad,
	} = useCesium();

	const [isOpen, setIsOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<'layers' | 'basemaps'>('layers');
	const [baseMapEnabled, setBaseMapEnabled] = useState(() => {
		const s = getSettings();
		return typeof s.baseMapEnabled === 'boolean' ? s.baseMapEnabled : true;
	});
	// Categorized Layers State
	const [layerCategories, setLayerCategories] = useState<LayerCategory[]>([]);

	// Expanded/Collapsed State for Groups
	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
		sensors: true,
		'3d-tiles': true,
		subsurface: true,
	});

	const activeBaseLayerPrimitive = useRef<any>(null);
	const currentViewerMapId = useRef<string | null>(null);

	const layerVisibilityMap = useRef<Map<string, boolean>>(new Map());

	// Close on map click
	useEffect(() => {
		if (!viewer || !isOpen) return;
		const handleMapInteraction = () => setIsOpen(false);
		if (!viewer.isDestroyed()) {
			viewer.scene.canvas.addEventListener('pointerdown', handleMapInteraction);
		}
		return () => {
			if (viewer && !viewer.isDestroyed()) {
				viewer.scene.canvas.removeEventListener('pointerdown', handleMapInteraction);
			}
		};
	}, [viewer, isOpen]);

	// Sync Layers Logic
	useEffect(() => {
		if (!viewer) return;

		const syncLayers = () => {
			// Check if viewer is destroyed or scene is missing before accessing primitives
			if (!viewer || viewer.isDestroyed() || !viewer.scene) return;

			const surfaceItems: LayerData[] = [];
			const subSurfaceItems = getSubsurfaceLayers();

			// Find 3D Tiles Primitives
			const primitives = viewer.scene.primitives;
			const seenIdentifiers = new Set<string>();
			const seenUniqueIds = new Set<string>();

			for (let i = 0; i < primitives.length; i++) {
				const prim = primitives.get(i);
				if (prim === activeBaseLayerPrimitive.current) continue;

				if (prim instanceof Cesium.Cesium3DTileset && (prim as any).name) {
					const name = (prim as any).name;
					const cesiumId = (prim as any).cesiumId;
					const uniqueKey = cesiumId ? `asset-${cesiumId}` : `name-${name}`;
					const isSubSurface = (prim as any).subSurface === true;

					if (seenIdentifiers.has(uniqueKey)) continue;
					seenIdentifiers.add(uniqueKey);
					if ((prim as any).uniqueId) seenUniqueIds.add((prim as any).uniqueId);

					const layerData: LayerData = {
						id: `prim-${i}`,
						uniqueKey: uniqueKey,
						type: 'primitive',
						name: name,
						visible: prim.show,
						reference: prim,
						thumbnail: cesiumId ? `https://assets.cesium.com/${cesiumId}/icon.png` : undefined,
						isSubSurface: isSubSurface,
						tilesetType: (prim as any).tilesetType ?? null,
					};

					if (!isSubSurface) {
						surfaceItems.push(layerData);
					}
				}
			}

			// Layers the user switched off are not in the scene at all. List them anyway,
			// so they can be switched back on — that is what triggers the fetch.
			deferredLayers.forEach((info) => {
				if (info.subSurface) return; // handled by the sub-surface group
				if (seenUniqueIds.has(info.id)) return;
				seenUniqueIds.add(info.id);

				surfaceItems.push({
					id: info.id,
					uniqueKey: info.id,
					type: 'primitive',
					name: info.name,
					visible: false,
					reference: null,
					deferred: true,
					thumbnail: info.cesiumId ? `https://assets.cesium.com/${info.cesiumId}/icon.png` : undefined,
					isSubSurface: false,
				});
			});

			// Build Categories
			const categories: LayerCategory[] = [];

			if (surfaceItems.length > 0) {
				categories.push({
					id: '3d-tiles',
					label: t('viewer.layers.groups.tiles'),
					icon: <Layers size={14} />,
					items: surfaceItems,
				});
			}

			if (subSurfaceItems.length > 0) {
				categories.push({
					id: 'subsurface',
					label: t('viewer.layers.groups.subsurface'),
					icon: <Layers size={14} />,
					items: subSurfaceItems,
				});
			}

			setLayerCategories(categories);
		};

		// Initial Sync
		if (isOpen || tilesLoaded || subSurfaceLayers.length > 0 || deferredLayers.length > 0) {
			syncLayers();
		}

		// Listen for DataSource additions/removals
		const dsAddedRemoveListener = viewer.dataSources.dataSourceAdded.addEventListener(syncLayers);
		const dsRemovedListener = viewer.dataSources.dataSourceRemoved.addEventListener(syncLayers);

		return () => {
			// Cleanup listeners
			dsAddedRemoveListener();
			dsRemovedListener();
		};
		// pendingLayerLoads: a deferred load that failed leaves the scene unchanged, so
		// its settling is the only signal to show the entry as switched off again.
	}, [viewer, tilesLoaded, isOpen, undergroundEnabled, subSurfaceLayers, deferredLayers, pendingLayerLoads, t]);

	useEffect(() => {
		if (!viewer || viewer.isDestroyed()) return;

		// If the user saved "Base Map Disabled", force the viewer to hide it immediately
		if (!baseMapEnabled) {
			viewer.scene.globe.show = false;
			viewer.imageryLayers.removeAll();
			if (activeBaseLayerPrimitive.current) {
				viewer.scene.primitives.remove(activeBaseLayerPrimitive.current);
				activeBaseLayerPrimitive.current = null;
			}
			// Usually disabling base map implies enabling underground mode for better visibility
			setUndergroundEnabled(true);
		}
	}, [viewer]); // Run once on mount (depend on viewer ready)

	// Layer visibility change (PostRender)
	useEffect(() => {
		if (!viewer) return;

		const handleSceneRender = () => {
			if (viewer.isDestroyed() || !viewer.scene) return;

			let anyChanged = false;
			const primitives = viewer.scene.primitives;

			for (let i = 0; i < primitives.length; i++) {
				const prim = primitives.get(i);
				if (!(prim instanceof Cesium.Cesium3DTileset)) continue;

				const id = (prim as any).uniqueId || `prim-${i}`;
				const previousVis = layerVisibilityMap.current.get(id);
				const currentVis = prim.show;

				// Detect visibility change
				if (previousVis !== undefined && previousVis !== currentVis) {
					anyChanged = true;
				}
				layerVisibilityMap.current.set(id, currentVis);
			}

			// Batch update UI if any visibility changed
			if (anyChanged) {
				setLayerCategories((prev) =>
					prev.map((cat) => ({
						...cat,
						items: cat.items.map((item) => ({
							...item,
							visible: item.reference ? (item.reference as any).show : item.visible,
						})),
					})),
				);
				if (!viewer.isDestroyed()) {
					viewer.scene.requestRender();
				}
			}
		};

		const removeListener = viewer.scene.postRender.addEventListener(handleSceneRender);

		return () => {
			removeListener();
		};
	}, [viewer]);

	useEffect(() => {
		if (!viewer || viewer.isDestroyed() || !selectedBaseMapId) return;

		if (!baseMapEnabled) return;

		// If the viewer is already showing this map, do nothing
		if (currentViewerMapId.current === selectedBaseMapId) return;

		const option = basemapOptions.find((opt) => opt.id === selectedBaseMapId);

		if (option) {
			console.log('Restoring BaseMap from storage:', selectedBaseMapId);

			const applyMap = async () => {
				if (viewer.isDestroyed()) return;

				// Cleanup existing primitive if switching
				if (activeBaseLayerPrimitive.current) {
					viewer.scene?.primitives.remove(activeBaseLayerPrimitive.current);
					activeBaseLayerPrimitive.current = null;
				}

				// Execute the specific handler (e.g., load Google 3D Tiles)
				await option.handler(viewer, activeBaseLayerPrimitive);

				// Mark as applied so we don't re-run logic unnecessarily
				currentViewerMapId.current = selectedBaseMapId;
			};

			applyMap();
		}
	}, [viewer, selectedBaseMapId, baseMapEnabled, basemapOptions]);

	const getSubsurfaceLayers = () => {
		if (!viewer || viewer.isDestroyed() || !subSurfaceLayers) return [];

		const subSurfaceItems: LayerData[] = [];

		subSurfaceLayers.forEach((layerInfo) => {
			let reference: any = null;
			let isVisible = false;

			if (layerInfo.type === 'datasource') {
				const dsArray = viewer.dataSources.getByName(layerInfo.id);

				if (dsArray.length > 0) {
					reference = dsArray[0];
					isVisible = reference.show;
				}
			} else if (layerInfo.type === 'primitive') {
				if (!viewer.scene) return;
				const primitives = viewer.scene.primitives;
				for (let i = 0; i < primitives.length; i++) {
					const p = primitives.get(i);
					if ((p as any).uniqueId === layerInfo.id) {
						reference = p;
						isVisible = p.show;
						break;
					}
				}
			}

			if (reference) {
				subSurfaceItems.push({
					id: layerInfo.id,
					uniqueKey: layerInfo.id,
					type: layerInfo.type,
					name: layerInfo.name,
					visible: isVisible,
					reference: reference,
					isSubSurface: true,
				});
				return;
			}

			// Nothing in the scene: it is either still loading or deliberately deferred.
			const deferred = deferredLayers.find((l) => l.id === layerInfo.id);
			if (deferred) {
				subSurfaceItems.push({
					id: layerInfo.id,
					uniqueKey: layerInfo.id,
					type: layerInfo.type,
					name: deferred.name || layerInfo.name,
					visible: false,
					reference: null,
					deferred: true,
					isSubSurface: true,
				});
			}
		});

		return subSurfaceItems;
	};

	const toggleGroup = (groupId: string) => {
		setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
	};

	const toggleGroupVisibility = (e: React.MouseEvent, category: LayerCategory) => {
		e.stopPropagation();
		if (!viewer || viewer.isDestroyed()) return;

		const isAnyVisible = category.items.some((item) => item.visible);
		const targetState = !isAnyVisible;

		const visibilityUpdates: Record<string, boolean> = {};

		category.items.forEach((item) => {
			if (item.isSubSurface && !undergroundEnabled) return;
			const persistentId = (item.reference as any)?.uniqueId || item.uniqueKey || item.id;
			visibilityUpdates[persistentId] = targetState;

			if (!item.reference) {
				// Not built yet — switching it on is what makes TilesLoader fetch it.
				if (targetState) requestLayerLoad(persistentId);
				return;
			}
			item.reference.show = targetState;
		});

		setLayerCategories((prev) =>
			prev.map((cat) => {
				if (cat.id !== category.id) return cat;
				return {
					...cat,
					items: cat.items.map((item) => {
						if (item.isSubSurface && !undergroundEnabled) return item;
						return { ...item, visible: targetState };
					}),
				};
			}),
		);
		updateSetting('layerVisibility', visibilityUpdates);

		viewer.scene.requestRender();
	};

	const toggleLayerVisibility = (layer: LayerData) => {
		if (!viewer || viewer.isDestroyed()) return;
		if (layer.isSubSurface && !undergroundEnabled) return;

		const persistentId = (layer.reference as any)?.uniqueId || layer.uniqueKey || layer.id;

		// Deferred layer: remember the choice and let TilesLoader pull the tileset in.
		if (!layer.reference) {
			updateSetting('layerVisibility', { [persistentId]: true });
			requestLayerLoad(persistentId);
			setLayerCategories((prev) =>
				prev.map((cat) => ({
					...cat,
					items: cat.items.map((item) => (item.id === layer.id ? { ...item, visible: true } : item)),
				})),
			);
			return;
		}

		const nextVisible = !layer.reference.show;
		layer.reference.show = nextVisible;
		updateSetting('layerVisibility', { [persistentId]: nextVisible });
		setLayerCategories((prev) =>
			prev.map((cat) => ({
				...cat,
				items: cat.items.map((item) => (item.id === layer.id ? { ...item, visible: nextVisible } : item)),
			})),
		);
		viewer.scene.requestRender();
	};

	const zoomToLayer = (layer: LayerData) => {
		if (!viewer || viewer.isDestroyed()) return;
		if (!layer.visible || !layer.reference) return;

		viewer.flyTo(layer.reference as any, { duration: 1.5 }).catch((e) => console.warn('Could not fly to layer', e));
	};

	const enableBaseMap = async (enable: boolean) => {
		if (!viewer || viewer.isDestroyed()) return;
		if (enable) {
			await changeBaseLayer(basemapOptions.find((opt) => opt.id === selectedBaseMapId)!);
			setUndergroundEnabled(false);
		} else {
			viewer.scene.globe.show = false;
			viewer.imageryLayers.removeAll();
			if (activeBaseLayerPrimitive.current) {
				viewer.scene.primitives.remove(activeBaseLayerPrimitive.current);
				activeBaseLayerPrimitive.current = null;
			}
			setUndergroundEnabled(true);
		}

		setBaseMapEnabled(enable);
		updateSetting('baseMapEnabled', enable);
	};

	const changeBaseLayer = async (option: BaseMapOption) => {
		if (!viewer || viewer.isDestroyed()) return;
		currentViewerMapId.current = option.id;

		if (activeBaseLayerPrimitive.current) {
			viewer.scene?.primitives.remove(activeBaseLayerPrimitive.current);
			activeBaseLayerPrimitive.current = null;
		}
		await option.handler(viewer, activeBaseLayerPrimitive);
		updateSetting('baseMapId', option.id);

		if (onBaseMapChange) {
			onBaseMapChange(option.id);
		}
	};

	return (
		<div className="absolute bottom-12 left-4 z-[50] flex flex-col items-start gap-2 font-sans text-foreground">
			{/* Panel */}
			{isOpen && (
				<div
					className={`${undergroundEnabled ? 'mb-13' : 'mb-9'} bg-card/95 backdrop-blur-md rounded-xl shadow-2xl w-80 border border-foreground/20 overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-300 origin-bottom-left`}
				>
					{/* Header Tabs */}
					<div className="flex bg-muted/30 p-1 gap-1 m-2 rounded-lg">
						<TabButton
							active={activeTab === 'layers'}
							onClick={() => setActiveTab('layers')}
							icon={<Layers size={14} />}
							label={t('viewer.layers.tabs.layers')}
						/>
						<TabButton
							active={activeTab === 'basemaps'}
							onClick={() => setActiveTab('basemaps')}
							icon={<Globe size={14} />}
							label={t('viewer.layers.tabs.baseMaps')}
						/>
					</div>

					<div className="p-3 pt-0 max-h-[500px] overflow-y-auto custom-scrollbar">
						{activeTab === 'layers' && (
							<div className="space-y-1">
								{layerCategories.length === 0 && (
									<div className="text-center py-8 text-muted-foreground text-sm">{t('viewer.layers.empty')}</div>
								)}

								{layerCategories.map((category) => {
									const isGroupPartiallyVisible = category.items.some((i) => i.visible);
									const GroupIcon = isGroupPartiallyVisible ? Eye : EyeOff;

									return (
										<div
											key={category.id}
											className="border border-foreground/10 rounded-lg overflow-hidden bg-card shadow-sm"
										>
											<div className="flex items-center bg-muted/30 hover:bg-muted/50 transition-colors pr-2">
												<button
													onClick={(e) => toggleGroupVisibility(e, category)}
													className={`p-2.5 hover:text-primary transition-colors z-10 ${
														isGroupPartiallyVisible ? 'text-primary' : 'text-muted-foreground'
													}`}
													title={isGroupPartiallyVisible ? t('viewer.layers.hideGroup') : t('viewer.layers.showGroup')}
												>
													<GroupIcon size={16} />
												</button>

												<button
													onClick={() => toggleGroup(category.id)}
													className="flex-1 flex items-center justify-between py-2 pl-0"
												>
													<div className="flex items-center gap-2 text-foreground font-semibold text-xs uppercase tracking-wide">
														{category.icon}
														<span className="font-bold">{category.label}</span>
														<span className="bg-muted text-muted-foreground text-[10px] px-1.5 rounded-full">
															{category.items.length}
														</span>
													</div>
													{expandedGroups[category.id] ? (
														<ChevronDown size={14} className="text-muted-foreground/70" />
													) : (
														<ChevronRight size={14} className="text-muted-foreground/70" />
													)}
												</button>
											</div>

											{expandedGroups[category.id] && (
												<div className={'space-y-1 p-1 bg-card'}>
													{category.items.map((layer) => (
														<LayerItem
															key={layer.id}
															layer={layer}
															undergroundEnabled={undergroundEnabled}
															onToggle={() => toggleLayerVisibility(layer)}
															onZoom={() => zoomToLayer(layer)}
														/>
													))}
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}

						{activeTab === 'basemaps' && (
							<div className="space-y-2 mt-1">
								<div className="flex items-center justify-between px-3 py-2 border-b border-foreground/10 mb-2 bg-muted/30 rounded-t-lg">
									<span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
										{t('viewer.layers.enableBasemap')}
									</span>
									<label className="flex items-center justify-between cursor-pointer group select-none">
										<input
											type="checkbox"
											className="sr-only"
											checked={baseMapEnabled}
											onChange={(e) => enableBaseMap(e.target.checked)}
										/>
										<div
											className={`w-9 h-5 rounded-full transition-colors duration-200 ease-in-out ${
												baseMapEnabled ? 'bg-primary' : 'bg-foreground/20'
											}`}
										>
											<div
												className={`h-4 w-4 bg-background rounded-full shadow transform transition-transform mt-0.5 ml-0.5 ${
													baseMapEnabled ? 'translate-x-4' : ''
												}`}
											/>
										</div>
									</label>
								</div>

								<div
									className={`space-y-1 transition-opacity duration-200 ${baseMapEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none grayscale'}`}
								>
									{basemapOptions.map((opt) => (
										<button
											key={opt.id}
											onClick={() => changeBaseLayer(opt)}
											className={`
												w-full cursor-pointer flex items-center gap-3 p-2 rounded-lg border transition-all text-left group
												${
													selectedBaseMapId === opt.id
														? 'bg-primary/10 border-primary/50 shadow-sm'
														: 'bg-card border-transparent hover:bg-muted/50'
												}
											`}
										>
											<div className="w-8 h-8 rounded bg-muted flex items-center justify-center shrink-0 border border-border overflow-hidden">
												<Globe size={14} className="text-muted-foreground" />
											</div>
											<div className="flex-1 min-w-0">
												<div
													className={`text-xs font-semibold truncate ${selectedBaseMapId === opt.id ? 'text-primary' : 'text-foreground'}`}
												>
													{opt.name}
												</div>
												<div className="text-[10px] text-muted-foreground truncate">{opt.description}</div>
											</div>
											{selectedBaseMapId === opt.id && <Check size={14} className="text-primary" />}
										</button>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			<button
				onClick={() => setIsOpen(!isOpen)}
				className="bg-card text-foreground p-3 rounded-lg shadow-xl hover:bg-muted/50 border border-border transition-all active:scale-95"
			>
				{isOpen ? <X size={24} /> : <Layers size={24} />}
			</button>
		</div>
	);
};

const TabButton = ({ active, onClick, icon, label }: any) => (
	<button
		onClick={onClick}
		className={`
            flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-xs font-semibold transition-all
            ${active ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}
        `}
	>
		{icon} {label}
	</button>
);

const LayerItem = ({
	layer,
	undergroundEnabled,
	onToggle,
	onZoom,
}: {
	layer: LayerData;
	undergroundEnabled: boolean;
	onToggle: () => void;
	onZoom: () => void;
}) => {
	const { t } = useTranslation();
	// Locked if it's sub-surface AND underground mode is OFF
	const isLocked = layer.isSubSurface && !undergroundEnabled;

	const { entries: ifcEntries, openPanel } = useIfcFeatures();
	const {
		selectedDamage,
		selectedDamageEntity,
		setSelectedDamage,
		setSelectedDamageEntity,
		drawMode,
		setDrawMode,
	} = useCesium();
	const ifcEntry =
		layer.tilesetType === 'bim' && layer.reference
			? ifcEntries[(layer.reference as any).uniqueId as string]
			: undefined;
	// A damage being drawn or edited keeps the top-right slot until it is saved or cancelled.
	const damageDrawing = drawMode === 'INSERT' || drawMode === 'EDIT';
	const structureDisabled = ifcEntry?.status !== 'ready' || !layer.visible || damageDrawing;
	const structureTitle =
		ifcEntry?.status === 'loading'
			? t('viewer.ifc.structure.loading')
			: ifcEntry?.status === 'error'
				? t('viewer.ifc.structure.unavailableShort')
				: damageDrawing
					? t('viewer.ifc.structure.finishDamageFirst')
					: t('viewer.ifc.tabs.structure');

	return (
		<div
			className={`group flex items-center gap-3 p-1 rounded-lg border transition-colors border-transparent
            ${isLocked ? 'opacity-50 bg-muted/30 grayscale' : 'hover:bg-muted/30 hover:border-border'}
        `}
		>
			<button
				onClick={onToggle}
				disabled={isLocked}
				className={`p-1.5 rounded-md transition-colors ${
					isLocked
						? 'text-muted-foreground cursor-not-allowed'
						: layer.visible
							? 'text-primary bg-primary/10'
							: 'text-muted-foreground hover:bg-muted'
				}`}
				title={isLocked ? t('viewer.layers.enableSubSurfaceMode') : t('viewer.layers.toggleVisibility')}
			>
				{isLocked ? <Lock size={14} /> : layer.visible ? <Eye size={16} /> : <EyeOff size={16} />}
			</button>

			<div className="flex items-center gap-3 flex-1 min-w-0">
				<span className="text-xs font-semibold text-foreground truncate" title={layer.name}>
					{layer.name}
				</span>
			</div>

			{ifcEntry && (
				<button
					onClick={() => {
						// Same single-inspector rule: close an open damage popup in this same update.
						// Leaving it to the CANCEL effect is not enough: one render later the slot
						// effect in CesiumViewer still sees the old damage and closes the panel again,
						// and drawMode stays CANCEL after a popup closes, so setting it again would
						// not close the next one. No drawing can be running: the button is disabled
						// while one is.
						if (selectedDamage || selectedDamageEntity) {
							setSelectedDamage(null);
							setSelectedDamageEntity(null);
							setDrawMode('CANCEL');
						}
						openPanel(ifcEntry.key, 'structure');
					}}
					disabled={structureDisabled}
					className={`p-1.5 rounded-md transition-all ${
						structureDisabled
							? 'text-muted-foreground/30 cursor-not-allowed'
							: 'text-muted-foreground hover:text-primary hover:bg-primary/10 ' +
								'opacity-0 group-hover:opacity-100'
					}`}
					title={structureTitle}
				>
					<ListTree size={16} />
				</button>
			)}

			<button
				onClick={onZoom}
				disabled={!layer.visible}
				className={`p-1.5 rounded-md transition-all ${
					!layer.visible
						? 'text-muted-foreground/30 cursor-not-allowed'
						: 'text-muted-foreground hover:text-primary hover:bg-primary/10 opacity-0 group-hover:opacity-100'
				}`}
				title={t('viewer.layers.zoomToLayer')}
			>
				<ZoomIn size={16} />
			</button>
		</div>
	);
};

export default LayerControl;
